import React from 'react';
import Tooltip from './Tooltip';

// Etapas do fluxo de uma campanha de ofertas (ver CONTEXTO.md "Fluxo da Campanha").
// A fase é DERIVADA dos dados (status + nº de produtos + nº de aprovados + erp_oferta_oid),
// então não precisa de coluna nova no banco nem de endpoint dedicado.
const ETAPAS_BASE = [
  { chave: 'criada', label: 'Rascunho criado' },
  { chave: 'produtos', label: 'Produtos adicionados' },
  { chave: 'aprovados', label: 'Preço e aprovação' },
  { chave: 'ativa', label: 'Campanha ativa' },
  { chave: 'erp', label: 'Enviada ao ERP' },
];

// Calcula a fase atual e o estado (done/atual/pendente) de cada etapa.
export function calcularFaseCampanha(campanha, qtdProdutos = 0, qtdAprovados = 0) {
  const status = campanha?.status || 'rascunho';
  const temProdutos = (qtdProdutos || 0) > 0;
  const temAprovados = (qtdAprovados || 0) > 0;
  const ativaOuEncerrada = status === 'ativa' || status === 'encerrada';
  const enviadaErp = !!campanha?.erp_oferta_oid;
  const temDatas = !!(campanha?.data_inicio && campanha?.data_fim);
  const especificas = campanha?.escopo === 'especificas';

  // Nível = etapa mais avançada já alcançada (cascata monotônica — ativar exige aprovar,
  // enviar ao ERP exige estar ativa, então o avanço nunca "volta").
  let nivel = 1;
  if (temProdutos) nivel = 2;
  if (temAprovados) nivel = 3;
  if (ativaOuEncerrada) nivel = Math.max(nivel, 4);
  if (enviadaErp) nivel = 5;

  const hints = {
    criada: 'Campanha criada como rascunho.',
    produtos: temProdutos ? `${qtdProdutos} produto(s) na campanha.` : 'Adicione produtos do BDENTER (busca ou orçamento).',
    aprovados: temAprovados ? `${qtdAprovados} produto(s) aprovado(s).` : 'A diretoria define o preço e aprova ao menos 1 produto.',
    ativa: ativaOuEncerrada
      ? (status === 'encerrada' ? 'Campanha encerrada (somente leitura).' : 'Campanha ativa.')
      : `Ative a campanha — exige datas${especificas ? ', filiais' : ''} e 1 produto aprovado.`,
    erp: enviadaErp
      ? `Ofertas criadas no ERP${campanha?.erp_enviado_em ? ' em ' + new Date(campanha.erp_enviado_em).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : ''}.`
      : `Envie as ofertas (à vista${campanha?.tem_preco_12x ? ' + 12x' : ''}) para o ERP.`,
  };

  const etapas = ETAPAS_BASE.map((e, i) => {
    const pos = i + 1;
    return { ...e, done: pos <= nivel, atual: pos === nivel + 1, hint: hints[e.chave] };
  });

  const completo = nivel >= ETAPAS_BASE.length;
  // Pendências que travam o avanço (úteis no modal)
  const pendencias = [];
  if (!temProdutos) pendencias.push('adicionar produtos');
  if (!temAprovados) pendencias.push('aprovar ao menos 1 produto');
  if (!temDatas) pendencias.push('definir datas de início e fim');

  return { etapas, nivel, completo, status, encerrada: status === 'encerrada', enviadaErp, pendencias };
}

export default function CampanhaTracker({ campanha, qtdProdutos = 0, qtdAprovados = 0, variant = 'header' }) {
  const { etapas, completo, encerrada, enviadaErp, pendencias } = calcularFaseCampanha(campanha, qtdProdutos, qtdAprovados);

  if (variant === 'modal') {
    return (
      <div>
        <div className="mb-3" style={{ fontSize: '0.85rem', color: '#555' }}>
          {completo
            ? <span><i className="bi bi-check-circle-fill me-1" style={{ color: '#2e7d5b' }}></i>Fluxo concluído — campanha enviada ao ERP.</span>
            : encerrada
              ? <span><i className="bi bi-lock-fill me-1" style={{ color: '#999' }}></i>Campanha encerrada{!enviadaErp ? ' — nunca enviada ao ERP.' : '.'}</span>
              : pendencias.length
                ? <span><i className="bi bi-hourglass-split me-1" style={{ color: 'var(--mostarda)' }}></i>Para avançar, falta: {pendencias.join(', ')}.</span>
                : <span><i className="bi bi-hourglass-split me-1" style={{ color: 'var(--mostarda)' }}></i>Em andamento.</span>}
        </div>
        {etapas.map((e, i) => {
          const chip = e.done
            ? { txt: 'Concluído', bg: '#e8f5ee', fg: '#2e7d5b' }
            : e.atual
              ? { txt: 'Próximo passo', bg: 'var(--mostarda-claro)', fg: 'var(--mostarda)' }
              : { txt: 'Pendente', bg: '#f2f2f2', fg: '#999' };
          return (
            <div key={e.chave} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', position: 'relative', paddingBottom: i < etapas.length - 1 ? 16 : 0 }}>
              {i < etapas.length - 1 && (
                <div style={{ position: 'absolute', left: 13, top: 28, bottom: 0, width: 2, background: e.done ? 'var(--mostarda)' : '#e3e3e3' }} />
              )}
              <div className={`trk-dot ${e.done ? 'done' : ''} ${e.atual ? 'atual' : ''}`} style={{ marginTop: 1 }}>
                {e.done ? <i className="bi bi-check-lg"></i> : i + 1}
              </div>
              <div style={{ flex: 1 }}>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span style={{ fontWeight: 600, color: e.done || e.atual ? 'var(--mostarda)' : '#888' }}>{e.label}</span>
                  <span style={{ fontSize: '0.68rem', fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: chip.bg, color: chip.fg }}>{chip.txt}</span>
                </div>
                <div style={{ fontSize: '0.8rem', color: '#777', marginTop: 1 }}>{e.hint}</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // variant === 'header' — stepper horizontal compacto, com tooltip por etapa
  return (
    <div className="trk">
      {etapas.map((e, i) => (
        <div key={e.chave} className={`trk-step ${e.done ? 'done' : ''}`}>
          <Tooltip texto={e.hint} largura={220}>
            <div className={`trk-dot ${e.done ? 'done' : ''} ${e.atual ? 'atual' : ''}`}>
              {e.done ? <i className="bi bi-check-lg"></i> : i + 1}
            </div>
          </Tooltip>
          <div className={`trk-label ${e.done || e.atual ? 'on' : ''}`}>{e.label}</div>
        </div>
      ))}
    </div>
  );
}
